import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Send } from "lucide-react"

import { api, ErrorApi } from "@/api/cliente"
import { Boton } from "@/componentes/ui/Boton"
import { Modal } from "@/componentes/ui/Modal"
import { Cabecera, Cuerpo, Fila, Tabla, Td, Th } from "@/componentes/ui/Tabla"
import { comoFecha } from "@/lib/fechas"

import { esCuentaBloqueada, PinEnLinea } from "./CuentaBloqueada"
import { aCentimos, comoMonto, monto } from "./dinero"
import type { Cuenta } from "./tipos"

type Movimiento = {
  id: number
  fecha_valor: string
  tipo: "INGRESO" | "EGRESO"
  monto: string
  concepto: string
  referencia: string | null
}

type Estado = {
  cuenta_2: string
  saldo_3: string
  saldo_2: string | null
  saldo_2_despues: string | null
  error_2: string | null
  por_enviar: Movimiento[]
  por_anular: Movimiento[]
  enviados: number
  /** La cuenta puente (BBVA Provincial): se iguala con un ajuste, no se replica. */
  puentes: { nombre: string; saldo_3: string; saldo_2: string | null; ajuste: string | null }[]
}

/**
 * «Enviar al 2.0» (19/10): replica en el Sistema 2.0 los movimientos de esta cuenta
 * para que los dos saldos coincidan. Ver `services/sistema2_envio.py`.
 *
 * ⚠ **Con revisión, decisión del usuario**: enseña lo que falta por enviar y por anular,
 * los dos saldos y cómo quedará el del 2.0; nada sale hasta pulsar «Enviar». Si el saldo
 * proyectado no coincide con el del 3.0 se dice en rojo: alguien movió el 2.0 a mano.
 */
export function DialogoEnvioSistema2({
  cuenta,
  divisa,
  onCerrar,
}: {
  cuenta: Cuenta
  divisa: string
  onCerrar: () => void
}) {
  const clienteQuery = useQueryClient()
  const estado = useQuery({
    queryKey: ["sistema2-envio", cuenta.id],
    queryFn: () => api.get<Estado>(`/api/v1/cuentas-bancarias/${cuenta.id}/sistema2-envio`),
    retry: false,
  })
  const enviar = useMutation({
    meta: { exito: "Enviado al 2.0" },
    mutationFn: () =>
      api.post<{ enviados: number; anulados: number; ajustes: string[]; estado: Estado }>(
        `/api/v1/cuentas-bancarias/${cuenta.id}/sistema2-envio`,
        {},
      ),
    onSuccess: (r) => clienteQuery.setQueryData(["sistema2-envio", cuenta.id], r.estado),
  })

  const e = estado.data
  // Un puente descuadrado también es algo que enviar: su ajuste.
  const ajustes = (e?.puentes ?? []).filter((p) => p.ajuste !== null && aCentimos(p.ajuste) !== 0n)
  const pendientes = e ? e.por_enviar.length + e.por_anular.length + ajustes.length : 0
  const cuadra = e?.saldo_2_despues != null && aCentimos(e.saldo_2_despues) === aCentimos(e.saldo_3)

  return (
    <Modal
      abierto
      titulo="Enviar al Sistema 2.0"
      descripcion={`Replica los movimientos de ${cuenta.nombre} en ${e?.cuenta_2 ?? "el 2.0"} para que los dos saldos coincidan.`}
      onCerrar={onCerrar}
    >
      <div className="space-y-4">
        <PinEnLinea error={estado.error} onAbierta={() => estado.refetch()} />
        {estado.error instanceof ErrorApi && !esCuentaBloqueada(estado.error) && (
          <p className="text-sm" style={{ color: "var(--destructive)" }}>
            {estado.error.message}
          </p>
        )}
        {estado.isPending && <p className="text-sm">Cargando…</p>}

        {e && (
          <>
            <dl className="grid gap-3 sm:grid-cols-3">
              <Cifra etiqueta="Saldo en el 3.0" valor={`${monto(e.saldo_3)} ${divisa}`} />
              <Cifra
                etiqueta="Saldo en el 2.0 hoy"
                valor={e.saldo_2 === null ? "sin dato" : `${monto(e.saldo_2)} ${divisa}`}
              />
              <Cifra
                etiqueta="En el 2.0 tras enviar"
                valor={e.saldo_2_despues === null ? "—" : `${monto(e.saldo_2_despues)} ${divisa}`}
                tono={e.saldo_2_despues === null ? undefined : cuadra ? "ok" : "mal"}
              />
            </dl>
            {e.error_2 && (
              <p className="text-sm" style={{ color: "var(--warning)" }}>
                No se pudo leer el saldo del 2.0: {e.error_2}
              </p>
            )}
            {e.saldo_2_despues !== null && !cuadra && (
              <p className="text-sm" style={{ color: "var(--destructive)" }}>
                Tras enviar, los saldos no coincidirían: el 2.0 tiene movimientos que el 3.0 no
                tiene (o al revés). Revísalo antes de enviar.
              </p>
            )}

            {pendientes === 0 ? (
              <p className="tarjeta p-4 text-sm">
                El 2.0 ya tiene todo: {e.enviados} movimiento{e.enviados === 1 ? "" : "s"} enviado
                {e.enviados === 1 ? "" : "s"} desde el corte.
              </p>
            ) : (
              <Tabla etiqueta="Movimientos por enviar al 2.0">
                <Cabecera>
                  <Th>Fecha</Th>
                  <Th>Concepto</Th>
                  <Th derecha>Monto</Th>
                </Cabecera>
                <Cuerpo>
                  {e.por_enviar.map((m) => (
                    <Fila key={`e${m.id}`}>
                      <Td numero>{comoFecha(m.fecha_valor)}</Td>
                      <Td>{m.concepto}</Td>
                      <Td derecha numero>
                        <span
                          style={{
                            color: m.tipo === "INGRESO" ? "var(--success)" : "var(--destructive)",
                          }}
                        >
                          {m.tipo === "INGRESO" ? "+" : "−"}
                          {monto(m.monto)}
                        </span>
                      </Td>
                    </Fila>
                  ))}
                  {e.por_anular.map((m) => (
                    <Fila key={`a${m.id}`}>
                      <Td numero>{comoFecha(m.fecha_valor)}</Td>
                      <Td>
                        <span className="font-medium">Anular allá:</span> {m.concepto}
                      </Td>
                      <Td derecha numero>
                        <s>{monto(m.monto)}</s>
                      </Td>
                    </Fila>
                  ))}
                </Cuerpo>
              </Tabla>
            )}
          </>
        )}

        {e?.puentes.map((p) => {
          const dif = p.ajuste === null ? null : aCentimos(p.ajuste)
          return (
            <div key={p.nombre} className="tarjeta space-y-1 p-3 text-sm">
              <p className="font-medium">{p.nombre} · cuenta puente</p>
              <p className="tabular-nums" style={{ color: "var(--fg-muted)" }}>
                3.0: {monto(p.saldo_3)} · 2.0: {p.saldo_2 === null ? "sin dato" : monto(p.saldo_2)}
              </p>
              <p>
                {dif === null
                  ? "No se pudo leer el 2.0: no se ajusta."
                  : dif === 0n
                    ? "Coinciden: no hace falta ajuste."
                    : `Se enviará un «Ajuste de saldo» de ${dif > 0n ? "+" : "−"}${comoMonto(dif < 0n ? -dif : dif)} para dejarla igual que en el 3.0. Sus pagos de CxP y CxC se siguen registrando a mano allá.`}
              </p>
            </div>
          )
        })}

        {enviar.error instanceof ErrorApi && (
          <p className="text-sm" style={{ color: "var(--destructive)" }}>
            {enviar.error.message}
          </p>
        )}
        {enviar.data && (
          <p className="text-sm" style={{ color: "var(--success)" }}>
            Enviados {enviar.data.enviados}
            {enviar.data.anulados > 0 ? ` · anulados allá ${enviar.data.anulados}` : ""}
            {enviar.data.ajustes.length > 0 ? ` · ajuste: ${enviar.data.ajustes.join(", ")}` : ""}.
          </p>
        )}

        <div className="flex justify-end gap-2">
          <Boton onClick={onCerrar}>Cerrar</Boton>
          <Boton
            variante="primario"
            disabled={pendientes === 0 || enviar.isPending}
            onClick={() => enviar.mutate()}
          >
            <Send size={16} />
            {enviar.isPending ? "Enviando…" : "Enviar al 2.0"}
          </Boton>
        </div>
      </div>
    </Modal>
  )
}

function Cifra({
  etiqueta,
  valor,
  tono,
}: {
  etiqueta: string
  valor: string
  tono?: "ok" | "mal"
}) {
  return (
    <div className="tarjeta p-3">
      <dt className="text-xs uppercase" style={{ color: "var(--fg-muted)" }}>
        {etiqueta}
      </dt>
      <dd
        className="text-base font-semibold tabular-nums"
        style={{
          color:
            tono === "ok" ? "var(--success)" : tono === "mal" ? "var(--destructive)" : undefined,
        }}
      >
        {valor}
      </dd>
    </div>
  )
}
